import { isLauncherJourney } from '@epilot/journey-logic-commons/src/next/journey'
import type {
  Block,
  BlockId,
  Step,
  StepId
} from '@epilot/journey-logic-commons/src/types/next'

import { selectJourney } from '../selectors/config'
import { selectBlocksMapCache } from '../selectors/runtime'
import type { BlockValues } from '../types'
import type { SyncStepsStateAction } from '../types/actions'

import { actionFactory } from './factory'

export const syncStepsState = actionFactory<SyncStepsStateAction>(
  (state, { stepsState }) => {
    const journey = selectJourney(state)

    if (!journey) {
      throw new Error('Expected a Journey to be selected')
    }

    if (isLauncherJourney(journey)) {
      // mostly to coerce TS, should never happen
      throw new Error('Implementation error, launcher journeys have no state')
    }

    let blocksMapCache = selectBlocksMapCache(state)

    if (!blocksMapCache) {
      blocksMapCache = computeBlocksMap(
        journey?.steps ?? {},
        journey?.blocks ?? {}
      )

      state.journeys[journey.id].blocksMap = blocksMapCache
    }

    state.blockValues = stepsState.reduce<BlockValues>(
      (acc, stepState, stepIndex) => {
        for (const [blockName, value] of Object.entries(stepState)) {
          const block = blocksMapCache.get(stepIndex)?.get(blockName)

          if (block) {
            acc[block.id] = value
          }
        }

        return acc
      },
      {}
    )
  },
  'syncStepsState'
)

export const computeBlocksMap = (
  steps: Record<StepId, Step>,
  blocks: Record<BlockId, Block>
): Map<Step['orderIndex'], Map<Block['name'], Block>> => {
  const map: ReturnType<typeof computeBlocksMap> = new Map()

  for (const block of Object.values(blocks)) {
    const step = steps[block.stepId]
    const innerMap = map.get(step.orderIndex) ?? new Map<Block['name'], Block>()

    innerMap.set(block.name, block)

    if (!map.has(step.orderIndex)) {
      map.set(step.orderIndex, innerMap) // assigns
    }
  }

  return map
}
